import React from 'react';
import { Box, Typography, Paper, Grid, Chip, Rating, Divider, useTheme } from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  Hotel as HotelIcon,
  Business as BusinessIcon,
  SquareFoot as SquareFootIcon,
  Link as LinkIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { Hotel } from '@types';

interface BasicInformationTabProps {
  hotel: Hotel;
  isMobile: boolean;
}

const BasicInformationTab: React.FC<BasicInformationTabProps> = ({ hotel, isMobile }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: '0 0 8px 8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        transition: 'all 0.3s ease',
      }}
    >
      <Grid container spacing={3}>
        {/* Additional Property Details - Not shown in overview */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <HotelIcon sx={{ mr: 1 }} />
            Additional Property Details
          </Typography>

          {/* Operational & Investment Details */}
          <Paper sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" color="text.primary" sx={{
                fontWeight: 600,
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                pb: 1,
                display: 'inline-block'
              }}>
                Operational & Investment Details
              </Typography>
              <Chip
                label="Data Quality: High"
                size="small"
                color="success"
                variant="filled"
                sx={{ fontSize: '0.7rem', fontWeight: 600 }}
              />
            </Box>
            <Grid container spacing={3}>
              {hotel.chain_affiliation && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <BusinessIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Brand Affiliation
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{hotel.chain_affiliation}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Franchise/Management
                  </Typography>
                </Grid>
              )}

              {hotel.property_type && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <HotelIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Asset Class
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{hotel.property_type}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Property Classification
                  </Typography>
                </Grid>
              )}

              {hotel.num_rooms && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SquareFootIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Unit Mix
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{hotel.num_rooms} Rooms</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Revenue Generating Units
                  </Typography>
                </Grid>
              )}

              {hotel.amenities && hotel.amenities.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <BusinessIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Revenue Centers & Amenities
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {hotel.amenities.map((amenity, index) => {
                      // Identify revenue-generating amenities
                      const isRevenueCenter = ['restaurant', 'bar', 'spa', 'conference', 'meeting', 'banquet', 'catering', 'retail'].some(term =>
                        amenity.toLowerCase().includes(term)
                      );

                      return (
                        <Chip
                          key={index}
                          label={amenity}
                          size="small"
                          variant={isRevenueCenter ? "filled" : "outlined"}
                          color={isRevenueCenter ? "success" : "default"}
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: isRevenueCenter ? 600 : 400
                          }}
                        />
                      );
                    })}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    ● Green indicates revenue-generating amenities
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Contact & Online Presence */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle1" color="text.primary" gutterBottom sx={{
              fontWeight: 600,
              borderBottom: `2px solid ${theme.palette.secondary.main}`,
              pb: 1,
              mb: 2,
              display: 'inline-block'
            }}>
              Contact & Online Presence
            </Typography>
            <Grid container spacing={3}>
              {hotel.owner_info?.phone && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PhoneIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Phone
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{hotel.owner_info.phone}</Typography>
                </Grid>
              )}

              {hotel.website && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LanguageIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Website
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    component="a"
                    href={hotel.website.startsWith('http') ? hotel.website : `https://${hotel.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {hotel.website}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Detailed Location Information */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle1" color="text.primary" gutterBottom sx={{
              fontWeight: 600,
              borderBottom: `2px solid ${theme.palette.warning.main}`,
              pb: 1,
              mb: 2,
              display: 'inline-block'
            }}>
              Detailed Location Information
            </Typography>
            <Grid container spacing={3}>
              {/* Full Address Section */}
              {hotel.address && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocationOnIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Full Address
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>{hotel.address}</Typography>
                </Grid>
              )}

              {/* Location Components */}
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                  Location Details
                </Typography>
                <Grid container spacing={2}>
                  {hotel.city && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        City
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{hotel.city}</Typography>
                    </Grid>
                  )}

                  {hotel.state && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        State/Province
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{hotel.state}</Typography>
                    </Grid>
                  )}

                  {hotel.zip_code && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        ZIP/Postal Code
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{hotel.zip_code}</Typography>
                    </Grid>
                  )}

                  {hotel.country && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Country
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{hotel.country}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Description */}
        {hotel.description && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography variant="subtitle1" color="text.primary" gutterBottom sx={{
                fontWeight: 600,
                borderBottom: `2px solid ${theme.palette.info.main}`,
                pb: 1,
                mb: 2,
                display: 'inline-block'
              }}>
                Description
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {hotel.description}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default BasicInformationTab;
